import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { TwitterLogo, InstagramLogo, FacebookLogo } from '@phosphor-icons/react';
import { Container } from '@/components/common/Container';
import { FOOTER_LINKS, COMPANY_INFO, SOCIAL_LINKS } from '@/utils/constants';
import { theme } from '@/styles/theme';

const FooterWrapper = styled.footer`
  width: 100%;
  background: ${theme.colors.secondary.warm};
  padding: ${theme.spacing.xxxl} 0 ${theme.spacing.xl} 0;
  border-top: 1px solid ${theme.colors.neutral[200]};
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.xxl};
  margin-bottom: ${theme.spacing.xxl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FooterTitle = styled.h4`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.black};
  margin-bottom: ${theme.spacing.sm};
`;

const FooterText = styled.p`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.gray};
  line-height: 1.6;
  margin: 0;
`;

const FooterLink = styled(Link)`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.darkGray};
  text-decoration: none;
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary.main};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.medium};
  background: ${theme.colors.white};
  color: ${theme.colors.primary.main};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary.main};
    color: ${theme.colors.white};
    transform: translateY(-2px);
  }
`;

const Copyright = styled.div`
  padding-top: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.neutral[200]};
  text-align: center;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.gray};
`;

export const Footer = () => {
  return (
    <FooterWrapper>
      <Container>
        <FooterContent>
          <FooterColumn>
            <FooterTitle>{COMPANY_INFO.name}</FooterTitle>
            <FooterText>{COMPANY_INFO.tagline}</FooterText>
            <FooterText>
              Make vocabulary learning effortless, engaging, and joyful.
            </FooterText>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Navigation</FooterTitle>
            {FOOTER_LINKS.map((link) => (
              <FooterLink key={link.path} to={link.path}>
                {link.label}
              </FooterLink>
            ))}
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Connect</FooterTitle>
            <FooterText>Follow us on social media</FooterText>
            <SocialLinks>
              <SocialLink href={SOCIAL_LINKS.twitter} aria-label="Twitter">
                <TwitterLogo size={20} weight="fill" />
              </SocialLink>
              <SocialLink href={SOCIAL_LINKS.instagram} aria-label="Instagram">
                <InstagramLogo size={20} weight="fill" />
              </SocialLink>
              <SocialLink href={SOCIAL_LINKS.facebook} aria-label="Facebook">
                <FacebookLogo size={20} weight="fill" />
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
        </FooterContent>

        <Copyright>
          © {COMPANY_INFO.year} {COMPANY_INFO.name}. All rights reserved.
        </Copyright>
      </Container>
    </FooterWrapper>
  );
};
